'use client';

import React, { useEffect } from 'react';
import { Container } from 'components/shared';
import { BuyNowButton } from 'components/shared';
import { analytics } from 'lib/analytics/analytics';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { PHONE_MAX_WIDTH_IN_PX } from 'components/theme';
import { useTranslations } from 'next-intl';
import { LesMillsLogoWithType, UhLogoWithType } from './BrandMarks';
import { getLesMillsContent } from './content';
import { LES_MILLS_TRACKING } from './tracking';
import useLesMillsPricing from './useLesMillsPricing';
import styles from './Hero.module.css';

const Hero = () => {
  const t = useTranslations('uhXLesMills');
  const { hero } = React.useMemo(() => getLesMillsContent(t), [t]);
  const { bundlePriceDisplay } = useLesMillsPricing();
  const { width } = useWindowSize();
  const isMobile = width <= PHONE_MAX_WIDTH_IN_PX;
  const handleHeroBuyCtaClick = () => {
    analytics.track(LES_MILLS_TRACKING.cta.hero.event, {
      page: LES_MILLS_TRACKING.page,
      cta: 'hero',
    });
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const applyOrRemoveOffset = () => {
      if (!isMobile) {
        document.body.classList.remove('hero-chatbot-offset');
        return;
      }
      if (window.scrollY > 40) {
        document.body.classList.remove('hero-chatbot-offset');
      } else {
        document.body.classList.add('hero-chatbot-offset');
      }
    };
    applyOrRemoveOffset();
    window.addEventListener('scroll', applyOrRemoveOffset, { passive: true });
    return () => {
      window.removeEventListener(
        'scroll',
        applyOrRemoveOffset as EventListener,
      );
      document.body.classList.remove('hero-chatbot-offset');
    };
  }, [isMobile]);

  return (
    <section
      className={styles.heroSection}
      aria-label={t('a11y.heroSectionLabel')}
      style={
        {
          ['--hero-bg-mobile' as string]: `url(${getCompressedAssetUrl(
            '/web_v2/uh-x-les-mills/hero/mob-banner.png',
          )})`,
          ['--hero-bg-desktop' as string]: `url(${getCompressedAssetUrl(
            '/web_v2/uh-x-les-mills/hero/web-banner.png',
          )})`,
        } as React.CSSProperties
      }
    >
      <Container>
        <div className={styles.content}>
          <div className={styles.leftContent}>
            <div
              className={styles.brandRow}
              aria-label={t('a11y.brandRowLabel')}
              role="group"
            >
              <UhLogoWithType className={styles.uhLogoType} />
              <span className={styles.divider} />
              <LesMillsLogoWithType alt={t('a11y.lesMillsLogoAlt')} />
            </div>
            <h1 className={styles.title}>
              <span className={styles.titleLine}>{t('hero.titleLine1')}</span>{' '}
              <span className={styles.titleLine}>{t('hero.titleLine2')}</span>{' '}
              <span className={styles.titleLine}>{t('hero.titleLine3')}</span>
            </h1>
            <div className={styles.ctaRow}>
              <BuyNowButton
                className={`${styles.heroBuyNowButton} gtm`}
                href={hero.ctaHref}
                data-buttontype={LES_MILLS_TRACKING.cta.hero.dataButtonType}
                onClick={handleHeroBuyCtaClick}
                aria-label={t('a11y.buyBundleCtaAriaLabel', {
                  price: bundlePriceDisplay,
                })}
              >
                {t('common.buyBundleFor', { price: bundlePriceDisplay })}
              </BuyNowButton>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Hero;
